// "Alerta de clasificación de archivo" (Modo Auditor sección K, 2026-09-10)
// — mismo patrón que FormulariosPendientes.tsx: junta en el Dashboard lo que
// hoy solo calculaba un cron nocturno y solo se veía en el checklist de
// auditoría. Pacientes SIN la extensión archivo-estado (nunca corrió el
// barrido para ellos todavía, o son de alta muy reciente) — el médico los ve
// acá y puede reclasificarlos sin entrar a cada ficha.
import { useMedplum } from '@medplum/react-hooks';
import { formatHumanName } from '@medplum/core';
import type { Patient } from '@medplum/fhirtypes';
import type { JSX } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { notify } from '@/components/ui/sonner';
import { estadoArchivoDe } from '@/lib/archivoEstado';
import { reclasificarArchivoDePaciente } from '@/paciente/vaultClient';
import { claseTarjetaConPendientes, IndicadorPendiente } from './AlertaPendienteUI';

export function AlertaClasificacionArchivo(): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const navigate = useNavigate();
  const [pacientes, setPacientes] = useState<Patient[] | null>(null);
  const [abierto, setAbierto] = useState(false);
  const [reclasificando, setReclasificando] = useState<string | null>(null);

  const cargar = useCallback(async () => {
    try {
      const r = await medplum.searchResources('Patient', { _count: 100 });
      setPacientes((r as Patient[]).filter((p) => estadoArchivoDe(p) === undefined));
    } catch {
      setPacientes([]);
    }
  }, [medplum]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  const lista = pacientes ?? [];

  async function reclasificar(p: Patient): Promise<void> {
    if (!p.id) return;
    setReclasificando(p.id);
    try {
      const resultado = await reclasificarArchivoDePaciente(p.id);
      if (!resultado.estado) notify({ color: 'yellow', message: resultado.mensaje ?? t('dashboard.alertaArchivo.sinDatos') });
      await cargar();
    } catch (err) {
      notify({ color: 'red', message: err instanceof Error ? err.message : String(err), autoClose: false });
    } finally {
      setReclasificando(null);
    }
  }

  function nombreDe(p: Patient): string {
    return p.name?.[0] ? formatHumanName(p.name[0]) : (p.id ?? '—');
  }

  return (
    <div className={claseTarjetaConPendientes(lista.length > 0)}>
      <div className="flex items-center gap-1.5">
        {lista.length > 0 && <IndicadorPendiente />}
        <button type="button" onClick={() => setAbierto(true)} className="text-sm font-medium text-hce-ink hover:text-hce-pine hover:underline">
          {t('dashboard.alertaArchivo.titulo')} ({lista.length})
        </button>
      </div>
      <p className="mt-0.5 text-xs text-hce-muted">{t('dashboard.alertaArchivo.instruccion')}</p>

      <ul className="mt-2 space-y-1.5">
        {lista.length === 0 && <li className="text-xs text-hce-muted">{t('dashboard.alertaArchivo.sinPendientes')}</li>}
        {lista.slice(0, 5).map((p) => (
          <li key={p.id} className="flex items-center gap-2">
            <button type="button" onClick={() => void navigate(`/${p.id}/resumen`)} className="min-w-0 flex-1 truncate text-left text-sm text-hce-ink hover:text-hce-pine hover:underline">
              {nombreDe(p)}
            </button>
            <Button variant="ghost" size="sm" disabled={reclasificando === p.id} onClick={() => void reclasificar(p)}>
              {reclasificando === p.id ? t('dashboard.alertaArchivo.reclasificando') : t('dashboard.alertaArchivo.reclasificar')}
            </Button>
          </li>
        ))}
      </ul>

      <Dialog open={abierto} onOpenChange={setAbierto}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('dashboard.alertaArchivo.titulo')}</DialogTitle>
          </DialogHeader>

          {lista.length === 0 ? (
            <p className="py-3 text-sm text-hce-muted">{t('dashboard.alertaArchivo.sinPendientes')}</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('dashboard.alertaArchivo.paciente')}</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {lista.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell>
                      <button type="button" onClick={() => void navigate(`/${p.id}/resumen`)} className="text-hce-pine underline-offset-2 hover:underline">
                        {nombreDe(p)}
                      </button>
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" disabled={reclasificando === p.id} onClick={() => void reclasificar(p)}>
                        {reclasificando === p.id ? t('dashboard.alertaArchivo.reclasificando') : t('dashboard.alertaArchivo.reclasificar')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
